/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module VirtualServiceModule */

import {
    Component,
    Inject,
    Input,
    OnInit,
    Type,
} from '@angular/core';

import { skip, Subscription } from 'rxjs';
import { L10nService } from '@vmw/ngx-vip';
import { IVirtualService, WafMode } from 'generated-types';
import { WafPolicyCloneableItem, WafPolicyItem } from 'ajs/modules/waf/factories/waf-policy/waf-policy.item.factory';
import { DialogService } from 'ng/modules/core/services/dialog.service';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { VirtualServiceItem } from 'ng/modules/items/factories/virtual-service.item.factory';
import { TWafPolicyCloneableItem } from 'ajs/modules/waf/waf.types';
import {
    TWafPolicyCollection,
    WafPolicyCollection,
} from 'ajs/modules/waf/factories/waf-policy/waf-policy.collection.factory';

import { VsAttachWafInEvalModeComponent } from '../vs-attach-waf-in-eval-mode/vs-attach-waf-in-eval-mode.component';

import './virtual-service-preview.component.less';
import * as l10n from './virtual-service-preview.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const SYSTEM_WAF_EVALUATION_POLICY_NAME = 'System-WAF-Evaluation-Policy';

/**
 * @description
 * Component to show the custom Fields on Virtual Service preview.
 * @author Shanmukha Sarath Kondiparthi
 */
@Component({
    selector: 'virtual-service-preview',
    templateUrl: './virtual-service-preview.component.html',
})
export class VirtualServicePreviewComponent implements OnInit {
    /**
     * Virtual Service configuration.
     */
    @Input()
    public config: IVirtualService;

    /**
     * Flag that tells if editable is an item or a message item.
     */
    @Input()
    public readonly isItem: boolean;

    /**
     * Virtual Service item.
     */
    @Input()
    public readonly item: VirtualServiceItem;

    /**
     * Flag to show WAF evaluation preview.
     */
    public isWafPolicyNotAttached = false;

    public isEvalModeWafAttached = false;

    public wafPolicyName = '';

    private readonly wafPolicyCollection: WafPolicyCollection;

    public isVsBeingEdited = false;

    public isApplicationTypeChanged = false;

    public isApplicationTypeHttp = false;

    private onWafPolicyChangeSubscription: Subscription;

    private onApplicationTypeChangeSubscription: Subscription;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        private readonly dialogService: DialogService,
        private readonly authService: Auth,
        @Inject(WafPolicyCollection)
        WafPolicyCollection: TWafPolicyCollection,
        @Inject(WafPolicyCloneableItem)
        WafPolicyCloneable: TWafPolicyCloneableItem,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.wafPolicyCollection = new WafPolicyCollection({
            params: {
                mode: WafMode.WAF_MODE_EVALUATION,
            },
            itemClass: WafPolicyCloneable,
        });
    }

    /** @override */
    public ngOnInit(): void {
        this.isWafPolicyNotAttached = !this.config.waf_policy_ref;
        this.onWafPolicyChangeSubscription = this.item.onWafPolicyItemChange$.pipe(skip(1)).subscribe(
            (wafPolicyItem: WafPolicyItem | undefined) => {
                if (wafPolicyItem) {
                    const isWafInEvalMode = this.isWafPolicyInEvaluationMode(wafPolicyItem);

                    this.isEvalModeWafAttached = isWafInEvalMode;
                    this.isWafPolicyNotAttached = false;
                } else {
                    this.isEvalModeWafAttached = false;
                    this.isWafPolicyNotAttached = true;
                }
            },
        );

        this.onApplicationTypeChangeSubscription = this.item.onApplicationTypeChange$.pipe(skip(1)).subscribe(
            (isHTTPType: boolean) => {
                this.isApplicationTypeChanged = true;
                this.isApplicationTypeHttp = isHTTPType;
            },
        );

        this.isVsBeingEdited = this.item.isVsBeingEdited();
    }

    /** @override */
    public ngOnDestroy(): void {
        this.onWafPolicyChangeSubscription.unsubscribe();
        this.onApplicationTypeChangeSubscription.unsubscribe();
    }

    /**
     * Dismiss WAF recommendation preview.
     */
    public dismissWafRecommendation(): void {
        this.isWafPolicyNotAttached = false;
    }

    /**
     * Return boolean base on application type.
     * Return boolean base on item application type if type not changed.
     */
    public get isTypeHttp(): boolean {
        return this.isApplicationTypeChanged ? this.isApplicationTypeHttp : this.item.isHTTP();
    }

    /**
     * Show WAF evaluation recommendation modal.
     */
    public async showWafEvaluationRecommendationModal(): Promise<void> {
        this.setWafPolicyName();

        const { wafPolicyName } = this;
        const WAF_ATTACH_DIALOG_ID = 'WAF_ATTACH_DIALOG_ID';
        const wafPolicyItem = await this.getWafPolicyIfAlreadyExists(this.wafPolicyName, true);
        const isWafPolicyPresent = Boolean(wafPolicyItem);

        this.dialogService.add({
            id: WAF_ATTACH_DIALOG_ID,
            component: VsAttachWafInEvalModeComponent as Type<Component>,
            componentProps: {
                wafPolicyName,
                isWafPolicyPresent,
                onConfirm: () => {
                    this.handleWafAttachment().then(() => {
                        this.dialogService.remove(WAF_ATTACH_DIALOG_ID);
                        this.item.submit();
                    }).catch((error: unknown) => {
                        this.item.errors = [error as string];
                        console.error(error);
                    });
                },
                onClose: () => {
                    this.dialogService.remove(WAF_ATTACH_DIALOG_ID);
                },
            },
        });
    }

    /**
     * Check if WAF policy is in evaluation mode.
     * @param wafPolicyItem
     * @returns boolean
     */
    private isWafPolicyInEvaluationMode(wafPolicyItem: WafPolicyItem): boolean {
        return wafPolicyItem?.config.mode === WafMode.WAF_MODE_EVALUATION;
    }

    /**
     * Helper function to set the WAF Policy name.
     */
    private setWafPolicyName(): void {
        const WAF_POLICY_NAME_PREFIX = 'waf-evaluation-policy';
        const virtualServiceName = this.config.name ?? '';
        const currentTenantName = this.authService.getTenantName();

        const wafPolicyName = `${WAF_POLICY_NAME_PREFIX}-${currentTenantName}-${virtualServiceName}`;

        this.wafPolicyName = wafPolicyName;
    }

    /**
     * Handle WAF attachment to VS.
     */
    private async handleWafAttachment(): Promise<void> {
        const wafPolicy = await this.cloneSystemWafPolicy();

        this.item.config.waf_policy_ref = wafPolicy.config.url;
        this.isWafPolicyNotAttached = false;
    }

    /**
     * Clone system WAF policy if not already exists.
     * @returns wafPolicyCloneableItem
     */
    private async cloneSystemWafPolicy(): Promise<WafPolicyCloneableItem> {
        const wafPolicyItem = await this.getWafPolicyIfAlreadyExists(this.wafPolicyName);

        if (!wafPolicyItem) {
            const { wafPolicyName } = this;
            const systemWafEvalPolicyCloneableItem: WafPolicyCloneableItem =
                this.wafPolicyCollection.getItemByName(SYSTEM_WAF_EVALUATION_POLICY_NAME);
            const clonedWafPolicyItem = systemWafEvalPolicyCloneableItem.clone(wafPolicyName);

            await clonedWafPolicyItem.save();

            return clonedWafPolicyItem;
        }

        return wafPolicyItem;
    }

    /**
     * Get WAF policy if already exists.
     * @param wafPolicyName
     * @param loadWafPolicies
     * @returns wafPolicyItem
     */
    private async getWafPolicyIfAlreadyExists(
        wafPolicyName: string,
        loadWafPolicies = false,
    ): Promise<WafPolicyCloneableItem | null> {
        if (loadWafPolicies) {
            await this.wafPolicyCollection.load();
        }

        return this.wafPolicyCollection.getItemByName(wafPolicyName);
    }
}
